import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Navbar } from "@/components/Navbar";
import MobileTabBar from "@/components/MobileTabBar";
import { AnnouncementsBanner } from "@/components/AnnouncementsBanner";
import { LocationPingHeartbeat } from "@/components/LocationPingHeartbeat";
import Login from "@/pages/Login";
import EngineerDashboard from "@/pages/EngineerDashboard";
import CrewChiefDashboard from "@/pages/CrewChiefDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSettings from "@/pages/AdminSettings";
import Profile from "@/pages/Profile";
import Timesheet from "@/pages/Timesheet";
import DirectReports from "@/pages/DirectReports";
import Reviews from "@/pages/Reviews";
import Tasks from "@/pages/Tasks";
import AircraftStatus from "@/pages/AircraftStatus";
import Schedule from "@/pages/Schedule";
import ShiftLog from "@/pages/ShiftLog";
import ShiftLogReport from "@/pages/ShiftLogReport";
import OnDuty from "@/pages/OnDuty";
import OnVacation from "@/pages/OnVacation";
import Announcements from "@/pages/Announcements";
import TimesheetTemplateAdmin from "@/pages/TimesheetTemplateAdmin";
import RepairApprovals from "@/pages/RepairApprovals";
import POOverview from "@/pages/POOverview";
import PartsRequests from "@/pages/PartsRequests";
import Maintenance from "@/pages/Maintenance";
import MaintScan from "@/pages/MaintScan";
import FleetBoard from "@/pages/FleetBoard";
import Planning from "@/pages/Planning";
import Handovers from "@/pages/Handovers";
import DutyLoginAlert from "@/components/DutyLoginAlert";
import GSEScan from "@/pages/GSEScan";
import Capital from "@/pages/Capital";
import GSE from "@/pages/GSE";
import MShop from "@/pages/MShop";
import KPI from "@/pages/KPI";
import Discussion from "@/pages/Discussion";
import CriticalSpares from "@/pages/CriticalSpares";
import SparesMonitoring from "@/pages/SparesMonitoring";
import PRControls from "@/pages/PRControls";
import ToolStatus from "@/pages/ToolStatus";
import SearchEngine from "@/pages/SearchEngine";
import TSHelper from "@/pages/TSHelper";
import HeavyCheckStatus from "@/pages/HeavyCheckStatus";
import MyFiles from "@/pages/MyFiles";
import FormGenerator from "@/pages/FormGenerator";
import FAQ from "@/pages/FAQ";
import "@/App.css";

const RoleRouter = () => {
  const { user } = useAuth();
  if (!user) return null;
  // Landing page for every user (engineer / crew_chief / admin) is now the
  // Clock In page. Admins and Crew Chiefs can still reach their KPI overview
  // via /admin-overview and /crew-chief-overview respectively.
  return <Navigate to="/clock" replace />;
};

/** Gate any route that renders leadership-only surfaces (/team, /schedule).
 * Users without `can_have_reports` are bounced to /clock. Backend still
 * re-validates authz on every mutation — this is just DOM hygiene. */
const TeamRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return null;
  if (!user.can_have_reports) return <Navigate to="/clock" replace />;
  return children;
};

const AppShell = ({ children }) => (
  <div className="min-h-screen bg-summit-canvas pb-20 md:pb-0 overflow-x-hidden">
    <Navbar />
    <AnnouncementsBanner />
    <LocationPingHeartbeat />
    <DutyLoginAlert />
    {children}
    <MobileTabBar />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/gse/scan/:eid" element={<GSEScan />} />
            <Route path="/maintenance/scan/:tail" element={<MaintScan />} />
            <Route path="/fleet/board" element={<FleetBoard />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppShell><RoleRouter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clock"
              element={
                <ProtectedRoute>
                  <AppShell><EngineerDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppShell><Profile /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timesheet"
              element={
                <ProtectedRoute>
                  <AppShell><Timesheet /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/faq"
              element={
                <ProtectedRoute>
                  <AppShell><FAQ /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-files"
              element={
                <ProtectedRoute>
                  <AppShell><MyFiles /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/form-generator"
              element={
                <ProtectedRoute>
                  <AppShell><FormGenerator /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={
                <ProtectedRoute>
                  <TeamRoute>
                    <AppShell><DirectReports /></AppShell>
                  </TeamRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviews"
              element={
                <ProtectedRoute>
                  <AppShell><Reviews /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <AppShell><Tasks /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet-status"
              element={
                <ProtectedRoute>
                  <AppShell><AircraftStatus /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <ProtectedRoute>
                  <AppShell><Schedule /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/shift-log"
              element={
                <ProtectedRoute>
                  <AppShell><ShiftLog /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/shift-log/report"
              element={
                <ProtectedRoute>
                  <AppShell><ShiftLogReport /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/on-duty"
              element={
                <ProtectedRoute>
                  <TeamRoute>
                    <AppShell><OnDuty /></AppShell>
                  </TeamRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/on-vacation"
              element={
                <ProtectedRoute>
                  <TeamRoute>
                    <AppShell><OnVacation /></AppShell>
                  </TeamRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/repair-approvals"
              element={
                <ProtectedRoute>
                  <AppShell><RepairApprovals /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/po-overview"
              element={
                <ProtectedRoute>
                  <AppShell><POOverview /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/parts"
              element={
                <ProtectedRoute>
                  <AppShell><PartsRequests /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/pr-controls"
              element={
                <ProtectedRoute>
                  <AppShell><PRControls /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/tool-status"
              element={
                <ProtectedRoute>
                  <AppShell><ToolStatus /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/search-engine"
              element={
                <ProtectedRoute>
                  <AppShell><SearchEngine /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/ts-helper"
              element={
                <ProtectedRoute>
                  <AppShell><TSHelper /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/maintenance"
              element={
                <ProtectedRoute>
                  <AppShell><Maintenance /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/planning"
              element={
                <ProtectedRoute>
                  <AppShell><Planning /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/handovers"
              element={
                <ProtectedRoute>
                  <AppShell><Handovers /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet/heavy-checks"
              element={
                <ProtectedRoute>
                  <AppShell><HeavyCheckStatus /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/capital"
              element={
                <ProtectedRoute>
                  <AppShell><Capital /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gse"
              element={
                <ProtectedRoute>
                  <AppShell><GSE /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/m-shop"
              element={
                <ProtectedRoute>
                  <AppShell><MShop /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/kpi"
              element={
                <ProtectedRoute>
                  <AppShell><KPI /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/critical-spares"
              element={
                <ProtectedRoute>
                  <AppShell><CriticalSpares /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores/spares-monitoring"
              element={
                <ProtectedRoute>
                  <AppShell><SparesMonitoring /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/discussion"
              element={
                <ProtectedRoute>
                  <AppShell><Discussion /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <ProtectedRoute>
                  <AppShell><Announcements /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leadership/template"
              element={
                <ProtectedRoute>
                  <AppShell><TimesheetTemplateAdmin /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-overview"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crew-chief-overview"
              element={
                <ProtectedRoute roles={["crew_chief", "admin"]}>
                  <AppShell><CrewChiefDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute roles={["admin", "crew_chief"]}>
                  <AppShell><AdminSettings /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
